import React, { FC, useRef, useState, useEffect } from 'react'
import { Platform, ScrollView, TouchableOpacity, View, Text, Image } from 'react-native'
import { useNavigate, useParams } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import { getDatePickerLocale } from '@/utils/datepickerLocale'
import 'react-datepicker/dist/react-datepicker.css'
import { getHomeInfo, checkUserStatus } from '@front/apis'
import { travelApi, TravelLogic, type IUpdateTravelParams, type IAirline, type IAirport, type ITravelData } from '@front/logic'
import {
    AppBackground,
    BottomActionContainer,
    Button,
    Drop,
    Input,
    ResponsiveLogo,
} from '@ui/components'

import {
    InputContainer,
    InputWrapper,
    Headline2,
    Headline5,
    BodyText,
    ErrorText,
    colors,
    DatePickerWrapper,
    DisabledButton,
    ButtonContent,
    DisabledButtonText
} from '@/aena/styles' // Importando los estilos del PetAdd

import { ReactComponent as PawIcon } from '@/assets/icons/paw_color.svg'
import { ReactComponent as EditIcon } from '@/assets/icons/paw_color.svg'

import { useAppSelector } from '@front/store'
import { HeaderWithBackAena } from '@/components/headerwithback'

// Tipos de viaje permitidos por mascota
const TRAVEL_TYPES = ['Cabina', 'Bodega'] as const
type TravelType = (typeof TRAVEL_TYPES)[number]

interface TravelEditForm {
    // Datos del viaje
    airline: string
    departureAirport: string
    arrivalAirport: string
    flightNumber: string
    locator: string
    flightDate: Date | null

    // Mascotas seleccionadas
    selectedPets: string[]
    petTravelTypes: Record<string, TravelType>

    // Documentos de exoneración
    ownerName: string
    phoneNumber: string
    contactEmail: string

    // Condiciones
    acceptsTravelConditions: boolean
    acceptsTransportConditions: boolean
    acceptsPrivacyPolicy: boolean

    // Firma
    signature: string | null
}

interface Pet {
    guid: string
    name: string
    avatar: string
    weight?: number
    stats?: { weightKg?: number }
}

export const AenaTravelEdit: FC = () => {
    const navigate = useNavigate()
    const { travelId } = useParams<{ travelId: string }>()
    const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null)
    const [homeData, setHomeData] = useState<any>({})
    const [userData, setUserData] = useState<any>({})
    const petGUID = useAppSelector((state) => state.user.selectedPet) ?? ''
    const [selectedPet, setSelectedPet] = useState<string>(petGUID)
    const scrollViewRef = useRef<ScrollView>(null)
    const petRefs = useRef<Record<string, View | null>>({})

    // Estados para datos de la BD
    const [airlines, setAirlines] = useState<IAirline[]>([])
    const [airports, setAirports] = useState<IAirport[]>([])
    const [loadingData, setLoadingData] = useState(true)
    const [loadingTravel, setLoadingTravel] = useState(true)
    const [travel, setTravel] = useState<ITravelData | null>(null)

    const [formData, setFormData] = useState<TravelEditForm>({
        airline: '',
        departureAirport: '',
        arrivalAirport: '',
        flightNumber: '',
        locator: '',
        flightDate: null,
        selectedPets: [],
        petTravelTypes: {},
        ownerName: '',
        phoneNumber: '',
        contactEmail: '',
        acceptsTravelConditions: false,
        acceptsTransportConditions: false,
        acceptsPrivacyPolicy: false,
        signature: null
    })

    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({})
    const [isDrawing, setIsDrawing] = useState(false)
    const [showSignatureConfirmation, setShowSignatureConfirmation] = useState(false)

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Cargar datos básicos
                const [homeResponse, userStatusResponse, airlinesResponse, airportsResponse] = await Promise.all([
                    getHomeInfo({ lat: 0, lon: 0 }),
                    checkUserStatus({ lat: 0, lon: 0 }),
                    travelApi.getAirlines(),
                    travelApi.getAirports()
                ])

                setHomeData(homeResponse.data)
                setUserData(userStatusResponse.data)
                setAirlines(airlinesResponse.data)
                setAirports(airportsResponse.data)

                const petToSelect = petGUID !== '' ? petGUID : homeResponse.data?.petList?.[0]?.guid ?? ''
                setSelectedPet(petToSelect)

                setLoadingData(false)

                // Cargar datos del travel
                if (travelId) {
                    setLoadingTravel(true)
                    const travelResponse = await travelApi.getTravelDetail({ travelId })
                    const travelData = travelResponse.data

                    setTravel(travelData)

                    // Poblar el formulario con los datos existentes
                    // Necesitamos esperar a que las aerolíneas y aeropuertos estén cargados
                    const airlineCode = airlinesResponse.data.find(a => a.name === travelData.airline)?.code || ''
                    const departureCode = extractAirportCode(travelData.departureAirport)
                    const arrivalCode = extractAirportCode(travelData.arrivalAirport)

                    setFormData({
                        airline: airlineCode,
                        departureAirport: departureCode,
                        arrivalAirport: arrivalCode,
                        flightNumber: travelData.flightNumber,
                        locator: travelData.locator,
                        flightDate: new Date(travelData.flightDate),
                        selectedPets: travelData.pets.map(pet => pet.guid),
                        petTravelTypes: travelData.pets.reduce((acc, pet) => {
                            acc[pet.guid] = pet.travelType
                            return acc
                        }, {} as Record<string, TravelType>),
                        ownerName: travelData.ownerName,
                        phoneNumber: travelData.phoneNumber,
                        contactEmail: travelData.contactEmail,
                        acceptsTravelConditions: true, // Asumimos que ya fueron aceptadas
                        acceptsTransportConditions: true,
                        acceptsPrivacyPolicy: true,
                        signature: null // La firma se debe hacer de nuevo
                    })

                    setLoadingTravel(false)
                }
            } catch (error) {
                console.error('Error loading data:', error)
                setLoadingData(false)
                setLoadingTravel(false)
            }
        }

        fetchData()
    }, [travelId, petGUID])

    // Funciones auxiliares para extraer códigos
    const extractAirlineCode = (airlineName: string): string => {
        const airline = airlines.find(a => a.name === airlineName)
        return airline?.code || ''
    }

    const extractAirportCode = (airportString: string): string => {
        // Formato esperado: "MAD - Madrid" o similar
        const match = airportString.match(/^([A-Z]{3})/)
        return match ? match[1] : ''
    }

    // ===== Helpers de mascotas / peso / tipos permitidos
    const getPetByGuid = (guid: string): Pet | undefined =>
        homeData?.petList?.find((p: Pet) => p.guid === guid)

    const getPetWeightKg = (pet?: Pet): number =>
        (pet?.weight ?? pet?.weight ?? 0) as number

    const allowedTypesForPet = (pet?: Pet): TravelType[] => {
        const w = getPetWeightKg(pet)
        return (w > 8 ? ['Bodega'] : ['Cabina', 'Bodega']) as TravelType[]
    }

    // ===== Selectores y entradas
    const mapAirlinesToSelectable = (airlines: IAirline[]) =>
        airlines.map((airline) => ({ title: airline.name, id: airline.code, data: airline.code }))

    const mapAirportsToSelectable = (airports: IAirport[]) =>
        airports.map((airport) => ({ title: `${airport.city} - ${airport.name} (${airport.code})`, id: airport.code, data: airport.code }))

    const findAirlineIndex = (airlines: IAirline[], val: string) =>
        airlines.findIndex((airline) => airline.code === val)

    const findAirportIndex = (airports: IAirport[], val: string) =>
        airports.findIndex((airport) => airport.code === val)

    const handleInputChange = (field: keyof TravelEditForm, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    // ===== Selección múltiple de mascotas
    const togglePetSelection = (guid: string) => {
        setFormData(prev => {
            const already = prev.selectedPets.includes(guid)
            if (already) {
                const { [guid]: _, ...rest } = prev.petTravelTypes
                return {
                    ...prev,
                    selectedPets: prev.selectedPets.filter(id => id !== guid),
                    petTravelTypes: rest
                }
            } else {
                const pet = getPetByGuid(guid)
                const options = allowedTypesForPet(pet)
                return {
                    ...prev,
                    selectedPets: [...prev.selectedPets, guid],
                    petTravelTypes: {
                        ...prev.petTravelTypes,
                        [guid]: (options[0] ?? 'Bodega')
                    }
                }
            }
        })
    }

    // ===== Firma (canvas)
    const getCanvasCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        const canvas = signatureCanvasRef.current
        if (!canvas) return { x: 0, y: 0 }

        const rect = canvas.getBoundingClientRect()
        const scaleX = canvas.width / rect.width
        const scaleY = canvas.height / rect.height

        let clientX: number, clientY: number
        if ('touches' in e) {
            const touch = e.touches[0] || e.changedTouches[0]
            clientX = touch.clientX
            clientY = touch.clientY
        } else {
            const me = e as React.MouseEvent<HTMLCanvasElement>
            clientX = me.clientX
            clientY = me.clientY
        }

        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        }
    }

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault()
        setIsDrawing(true)
        const canvas = signatureCanvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        const coords = getCanvasCoordinates(e)
        ctx.lineWidth = 2
        ctx.lineCap = 'round'
        ctx.strokeStyle = '#000000'
        ctx.beginPath()
        ctx.moveTo(coords.x, coords.y)
    }

    const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault()
        if (!isDrawing) return
        const canvas = signatureCanvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        const coords = getCanvasCoordinates(e)
        ctx.lineTo(coords.x, coords.y)
        ctx.stroke()
    }

    const stopDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault()
        setIsDrawing(false)
        const canvas = signatureCanvasRef.current
        if (!canvas) return
        const dataURL = canvas.toDataURL()
        setFormData(prev => ({ ...prev, signature: dataURL }))
    }

    const clearSignature = () => {
        const canvas = signatureCanvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        setFormData(prev => ({ ...prev, signature: null }))
    }

    // ===== Validación
    const validateForm = (): boolean => {
        const errors: { [key: string]: string } = {}

        // Datos del viaje
        if (!formData.airline) errors.airline = 'La aerolínea es obligatoria'
        if (!formData.departureAirport) errors.departureAirport = 'El aeropuerto de salida es obligatorio'
        if (!formData.arrivalAirport) errors.arrivalAirport = 'El aeropuerto de destino es obligatorio'
        if (!formData.flightNumber?.trim()) errors.flightNumber = 'El número de vuelo es obligatorio'
        if (!formData.locator?.trim()) errors.locator = 'El localizador es obligatorio'
        if (!formData.flightDate) errors.flightDate = 'La fecha de vuelo es obligatoria'

        // Mascotas
        if (formData.selectedPets.length === 0) {
            errors.selectedPets = 'Debes seleccionar al menos una mascota'
        } else {
            for (const guid of formData.selectedPets) {
                const pet = getPetByGuid(guid)
                const val = formData.petTravelTypes[guid]
                const allowed = allowedTypesForPet(pet)
                if (!val) {
                    errors.selectedPets = 'Debes seleccionar el tipo de viaje para cada mascota'
                    break
                }
                if (!allowed.includes(val)) {
                    errors.selectedPets = `El tipo de viaje seleccionado no es válido para ${pet?.name}`
                    break
                }
            }
        }

        // Documentos
        if (!formData.ownerName?.trim()) errors.ownerName = 'El nombre es obligatorio'
        if (!formData.phoneNumber?.trim()) errors.phoneNumber = 'El teléfono es obligatorio'
        if (!formData.contactEmail?.trim()) errors.contactEmail = 'El email es obligatorio'

        // Condiciones
        if (!formData.acceptsTravelConditions) errors.acceptsTravelConditions = 'Debes aceptar las condiciones de viaje'
        if (!formData.acceptsTransportConditions) errors.acceptsTransportConditions = 'Debes aceptar las condiciones de transporte'
        if (!formData.acceptsPrivacyPolicy) errors.acceptsPrivacyPolicy = 'Debes aceptar las políticas de privacidad'

        // Firma
        if (!formData.signature) errors.signature = 'La firma es obligatoria'

        setFieldErrors(errors)
        return Object.keys(errors).length === 0
    }

    const handleSubmit = async () => {
        if (!validateForm() || !travelId) return

        setShowSignatureConfirmation(true)

        try {
            // Preparar datos para envío
            const updateData: IUpdateTravelParams = {
                id: travelId,
                airline: formData.airline,
                departureAirport: formData.departureAirport,
                arrivalAirport: formData.arrivalAirport,
                flightNumber: formData.flightNumber,
                locator: formData.locator,
                flightDate: formData.flightDate?.toISOString() || '',
                selectedPets: formData.selectedPets,
                petTravelTypes: formData.petTravelTypes,
                ownerName: formData.ownerName,
                phoneNumber: formData.phoneNumber,
                contactEmail: formData.contactEmail,
                acceptsTravelConditions: formData.acceptsTravelConditions,
                acceptsTransportConditions: formData.acceptsTransportConditions,
                acceptsPrivacyPolicy: formData.acceptsPrivacyPolicy,
                signature: formData.signature || '',
            }

            console.debug({ updateData })
            await travelApi.updateTravel(updateData)
            navigate(`aena/travel/detail/${travelId}`)
        } catch (error) {
            console.error('Error al actualizar el viaje:', error)
            alert('Error al actualizar el viaje. Inténtalo de nuevo.')
        } finally {
            setShowSignatureConfirmation(false)
        }
    }

    // Todas las mascotas seleccionadas tienen tipo
    const allPetsHaveType =
        formData.selectedPets.length > 0 &&
        formData.selectedPets.every(g => !!formData.petTravelTypes[g])

    const isFormValid =
        formData.acceptsTravelConditions &&
        formData.acceptsTransportConditions &&
        formData.acceptsPrivacyPolicy &&
        !!formData.signature &&
        allPetsHaveType

    // Loading states
    if (loadingData || loadingTravel) {
        return (
            <AppBackground isSafe>
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <BodyText>Cargando datos del viaje...</BodyText>
                </View>
            </AppBackground>
        )
    }

    if (!travel) {
        return (
            <AppBackground isSafe>
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
                    <BodyText style={{ textAlign: 'center', marginBottom: 16, color: '#dc3545' }}>
                        Viaje no encontrado
                    </BodyText>
                    <TouchableOpacity
                        onPress={() => navigate('aena/travel/list')}
                        style={{
                            paddingVertical: 12,
                            paddingHorizontal: 20,
                            borderRadius: 8,
                            backgroundColor: '#96ce00',
                            alignItems: 'center'
                        }}
                    >
                        <BodyText style={{ color: '#1a2732', fontWeight: '600' }}>Volver a la lista</BodyText>
                    </TouchableOpacity>
                </View>
            </AppBackground>
        )
    }

    // ===== Pantalla intermedia de "Procesando firmas"
    if (showSignatureConfirmation) {
        return (
            <AppBackground isSafe>
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
                    <View style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, alignItems: 'center', minWidth: 300 }}>
                        <Headline2 style={{ marginBottom: 20, textAlign: 'center' }}>
                            Actualizando viaje
                        </Headline2>

                        {formData.selectedPets.map(guid => {
                            const pet = getPetByGuid(guid)
                            return (
                                <View
                                    key={guid}
                                    style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16, width: '100%' }}
                                >
                                    <BodyText style={{ flex: 1 }}>{pet?.name}</BodyText>
                                    <View style={{ marginLeft: 16 }}>
                                        <Text style={{ fontSize: 20, color: '#96ce00' }}>⟳</Text>
                                    </View>
                                </View>
                            )
                        })}

                        <BodyText style={{ textAlign: 'center', marginTop: 16, color: '#666' }}>
                            Por favor, espera mientras actualizamos la información...
                        </BodyText>
                    </View>
                </View>
            </AppBackground>
        )
    }

    return (
        <AppBackground isSafe>
            <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
                {/* Logo */}
                <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
                    <ResponsiveLogo
                        breakpoint={820}
                        hysteresis={20}
                        large={{ width: 680, height: 96 }}
                        small={{ width: 360, height: 48 }}
                    />
                </View>

                <HeaderWithBackAena
                    title="Editar viaje"
                />

                <BottomActionContainer
                    bottomAction={
                        isFormValid ? (
                            <Button
                                title="Actualizar viaje"
                                icon={<EditIcon width={24} height={24} fill="#1a2732" />}
                                size="block"
                                textType="cta_medium"
                                borderRadius={5}
                                buttonWrapperStyle={{ backgroundColor: '#96ce00' }}
                                allowIconAndText
                                color="primary"
                                onPress={handleSubmit}
                            />
                        ) : (
                            <DisabledButton disabled>
                                <ButtonContent>
                                    <EditIcon width={24} height={24} fill="#1a2732" />
                                    <DisabledButtonText>Actualizar viaje</DisabledButtonText>
                                </ButtonContent>
                            </DisabledButton>
                        )
                    }
                >
                    <ScrollView>

                        {/* ================= SECCIÓN 1: DATOS DEL VIAJE ================= */}
                        <View style={{ marginBottom: 24 }}>
                            {/* Aerolínea */}
                            <InputWrapper>
                                <Headline5>Aerolínea *</Headline5>
                                <InputContainer>
                                    <Drop
                                        data={mapAirlinesToSelectable(airlines)}
                                        active={findAirlineIndex(airlines, formData.airline)}
                                        selectedItem={findAirlineIndex(airlines, formData.airline)}
                                        onPress={(item) => handleInputChange('airline', item.data)}
                                        placeholder={loadingData ? "Cargando aerolíneas..." : "Selecciona una aerolínea"}
                                        icon={<PawIcon width={16} height={16} fill="white" />}
                                        color="aena"
                                    />
                                    {fieldErrors.airline && <ErrorText>{fieldErrors.airline}</ErrorText>}
                                </InputContainer>
                            </InputWrapper>

                            {/* Aeropuertos */}
                            <View style={{ flexDirection: 'row', gap: 16 }}>
                                <View style={{ flex: 1 }}>
                                    <Headline5>Aeropuerto de Salida *</Headline5>
                                    <InputContainer>
                                        <Drop
                                            data={mapAirportsToSelectable(airports)}
                                            active={findAirportIndex(airports, formData.departureAirport)}
                                            selectedItem={findAirportIndex(airports, formData.departureAirport)}
                                            onPress={(item) => handleInputChange('departureAirport', item.data)}
                                            placeholder={loadingData ? "Cargando aeropuertos..." : "Aeropuerto origen"}
                                            icon={<PawIcon width={16} height={16} fill="white" />}
                                            color="aena"
                                        />
                                        {fieldErrors.departureAirport && <ErrorText>{fieldErrors.departureAirport}</ErrorText>}
                                    </InputContainer>
                                </View>

                                <View style={{ flex: 1 }}>
                                    <Headline5>Aeropuerto de Destino *</Headline5>
                                    <InputContainer>
                                        <Drop
                                            data={mapAirportsToSelectable(airports)}
                                            active={findAirportIndex(airports, formData.arrivalAirport)}
                                            selectedItem={findAirportIndex(airports, formData.arrivalAirport)}
                                            onPress={(item) => handleInputChange('arrivalAirport', item.data)}
                                            placeholder={loadingData ? "Cargando aeropuertos..." : "Aeropuerto destino"}
                                            icon={<PawIcon width={16} height={16} fill="white" />}
                                            color="aena"
                                        />
                                        {fieldErrors.arrivalAirport && <ErrorText>{fieldErrors.arrivalAirport}</ErrorText>}
                                    </InputContainer>
                                </View>
                            </View>

                            {/* Número de vuelo y Localizador */}
                            <View style={{ flexDirection: 'row', gap: 16 }}>
                                <View style={{ flex: 1 }}>
                                    <Headline5>Número de Vuelo *</Headline5>
                                    <InputContainer>
                                        <Input
                                            style={{ paddingRight: 0 }}
                                            inputProps={{
                                                value: formData.flightNumber,
                                                onChangeText: (text: string) => handleInputChange('flightNumber', text),
                                                placeholder: 'Ej: UX7791',
                                                style: { backgroundColor: '#e9ecef' }
                                            }}
                                        />
                                        {fieldErrors.flightNumber && <ErrorText>{fieldErrors.flightNumber}</ErrorText>}
                                    </InputContainer>
                                </View>

                                <View style={{ flex: 1 }}>
                                    <Headline5>Localizador *</Headline5>
                                    <InputContainer>
                                        <Input
                                            style={{ paddingRight: 0 }}
                                            inputProps={{
                                                value: formData.locator,
                                                onChangeText: (text: string) => handleInputChange('locator', text),
                                                placeholder: 'Ej: PI3vqz',
                                                style: { backgroundColor: '#e9ecef' }
                                            }}
                                        />
                                        {fieldErrors.locator && <ErrorText>{fieldErrors.locator}</ErrorText>}
                                    </InputContainer>
                                </View>
                            </View>

                            {/* Fecha de vuelo */}
                            <InputWrapper style={{ zIndex: 9999 }}>
                                <Headline5>Fecha de Vuelo *</Headline5>
                                <InputContainer>
                                    <DatePickerWrapper>
                                        <DatePicker
                                            portalId="root-portal"
                                            selected={formData.flightDate}
                                            onChange={(date: Date | null) => handleInputChange('flightDate', date)}
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText="Selecciona la fecha de vuelo"
                                            className="datepicker-styled"
                                            popperPlacement="bottom-start"
                                            minDate={new Date()}
                                            locale={getDatePickerLocale()}
                                            showMonthDropdown
                                            showYearDropdown
                                        />
                                    </DatePickerWrapper>
                                    {fieldErrors.flightDate && <ErrorText>{fieldErrors.flightDate}</ErrorText>}
                                </InputContainer>
                            </InputWrapper>
                        </View>

                        {/* ================= SECCIÓN 2: SELECCIÓN DE MASCOTAS ================= */}
                        <View style={{ marginBottom: 24 }}>
                            <Headline2 style={{ marginBottom: 16, color: colors.azulCorporativo }}>
                                ¿Quién viaja contigo?
                            </Headline2>

                            {/* Carrusel de mascotas */}
                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center' }}>
                                <ScrollView
                                    ref={scrollViewRef}
                                    horizontal
                                    scrollEventThrottle={16}
                                    showsHorizontalScrollIndicator={Platform.OS === 'web'}
                                    contentContainerStyle={{ paddingBottom: 16, display: 'flex' }}
                                    style={{
                                        position: 'relative',
                                        zIndex: 1,
                                        overflowX: Platform.OS === 'web' ? 'auto' : undefined,
                                        display: Platform.OS === 'web' ? 'flex' : undefined
                                    } as any}
                                >
                                    {homeData?.petList?.map((pet: Pet & { guid: string }) => {
                                        const isSelected = formData.selectedPets.includes(pet.guid)
                                        return (
                                            <TouchableOpacity
                                                key={pet.guid}
                                                onPress={() => {
                                                    setSelectedPet(pet.guid)
                                                    togglePetSelection(pet.guid)
                                                }}
                                                style={{ marginRight: 16, alignItems: 'center' }}
                                            >
                                                <View
                                                    style={{
                                                        padding: 3,
                                                        borderRadius: 53,
                                                        backgroundColor: '#ffffff',
                                                        borderWidth: 2,
                                                        borderColor: isSelected ? '#96ce00' : '#fff'
                                                    }}
                                                >
                                                    <Image
                                                        source={{ uri: pet.avatar }}
                                                        ref={(ref) => {
                                                            if (ref) petRefs.current[pet.guid] = ref
                                                        }}
                                                        style={{ width: 100, height: 100, borderRadius: 50 }}
                                                    />
                                                </View>
                                                <BodyText>{pet.name}</BodyText>
                                            </TouchableOpacity>
                                        )
                                    })}
                                </ScrollView>
                            </View>
                            {fieldErrors.selectedPets && <ErrorText>{fieldErrors.selectedPets}</ErrorText>}

                            {/* Listado de mascotas seleccionadas con selector por cada una */}
                            {formData.selectedPets.length > 0 && (
                                <View style={{ marginTop: 16, gap: 12 }}>
                                    {formData.selectedPets.map((guid) => {
                                        const pet = getPetByGuid(guid)
                                        const options = allowedTypesForPet(pet)
                                        const activeIdx = options.findIndex(o => o === formData.petTravelTypes[guid])
                                        return (
                                            <View
                                                key={guid}
                                                style={{
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    padding: 12,
                                                    borderWidth: 1,
                                                    borderColor: '#e0e0e0',
                                                    borderRadius: 12,
                                                    backgroundColor: 'white',
                                                    gap: 12
                                                }}
                                            >
                                                <Image
                                                    source={{ uri: pet?.avatar }}
                                                    style={{ width: 48, height: 48, borderRadius: 24 }}
                                                />
                                                <View style={{ flex: 1 }}>
                                                    <BodyText style={{ fontWeight: 'bold' }}>{pet?.name}</BodyText>
                                                    <BodyText style={{ color: '#666' }}>
                                                        Peso: {getPetWeightKg(pet)} kg
                                                    </BodyText>
                                                </View>

                                                <View style={{ minWidth: 160 }}>
                                                    <Drop
                                                        data={options.map(v => ({ id: v, title: v, data: v }))}
                                                        active={activeIdx}
                                                        selectedItem={activeIdx}
                                                        onPress={(item) =>
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                petTravelTypes: { ...prev.petTravelTypes, [guid]: item.data as TravelType }
                                                            }))
                                                        }
                                                        placeholder="Tipo de viaje"
                                                        icon={<PawIcon width={16} height={16} fill="white" />}
                                                        color="aena"
                                                    />
                                                </View>
                                            </View>
                                        )
                                    })}
                                    {/* Aviso de peso */}
                                    <BodyText style={{ marginTop: 12, fontStyle: 'italic', color: '#666' }}>
                                        * Los perros y gatos con un peso superior a 8 kg, incluido el transportín, no pueden transportarse en el interior de la cabina.
                                    </BodyText>
                                </View>
                            )}

                        </View>

                        {/* ================= SECCIÓN 3: DOCUMENTOS DE EXONERACIÓN ================= */}
                        <View style={{ marginBottom: 24 }}>
                            <Headline2 style={{ marginBottom: 16, color: colors.azulCorporativo }}>
                                Documentos de exoneración de responsabilidad
                            </Headline2>

                            <InputWrapper>
                                <Headline5>Nombre *</Headline5>
                                <InputContainer>
                                    <Input
                                        style={{ paddingRight: 0 }}
                                        inputProps={{
                                            value: formData.ownerName,
                                            onChangeText: (text: string) => handleInputChange('ownerName', text),
                                            placeholder: 'Introduce tu nombre completo',
                                            style: { backgroundColor: '#e9ecef' }
                                        }}
                                    />
                                    {fieldErrors.ownerName && <ErrorText>{fieldErrors.ownerName}</ErrorText>}
                                </InputContainer>
                            </InputWrapper>

                            <InputWrapper>
                                <Headline5>Nº de teléfono *</Headline5>
                                <InputContainer>
                                    <Input
                                        style={{ paddingRight: 0 }}
                                        inputProps={{
                                            value: formData.phoneNumber,
                                            onChangeText: (text: string) => handleInputChange('phoneNumber', text),
                                            placeholder: 'Introduce tu teléfono',
                                            keyboardType: 'numeric',
                                            style: { backgroundColor: '#e9ecef' }
                                        }}
                                    />
                                    {fieldErrors.phoneNumber && <ErrorText>{fieldErrors.phoneNumber}</ErrorText>}
                                </InputContainer>
                            </InputWrapper>

                            <InputWrapper>
                                <Headline5>Email de contacto *</Headline5>
                                <InputContainer>
                                    <Input
                                        style={{ paddingRight: 0 }}
                                        inputProps={{
                                            value: formData.contactEmail,
                                            onChangeText: (text: string) => handleInputChange('contactEmail', text),
                                            placeholder: 'Introduce tu email',
                                            keyboardType: 'email-address',
                                            style: { backgroundColor: '#e9ecef' }
                                        }}
                                    />
                                    {fieldErrors.contactEmail && <ErrorText>{fieldErrors.contactEmail}</ErrorText>}
                                </InputContainer>
                            </InputWrapper>
                        </View>

                        {/* ================= SECCIÓN 4: CONDICIONES ================= */}
                        <View style={{ marginBottom: 24 }}>
                            <InputWrapper>
                                <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                                    <CheckBoxCustom
                                        isChecked={formData.acceptsTravelConditions}
                                        onToggle={(val) => handleInputChange('acceptsTravelConditions', val)}
                                    />
                                    <BodyText style={{ flex: 1 }}>
                                        Acepto las condiciones de viaje con mascota
                                    </BodyText>
                                </View>
                                {fieldErrors.acceptsTravelConditions && <ErrorText>{fieldErrors.acceptsTravelConditions}</ErrorText>}
                            </InputWrapper>

                            <InputWrapper>
                                <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginTop: 12 }}>
                                    <CheckBoxCustom
                                        isChecked={formData.acceptsTransportConditions}
                                        onToggle={(val) => handleInputChange('acceptsTransportConditions', val)}
                                    />
                                    <BodyText style={{ flex: 1 }}>
                                        Acepto las condiciones de estado y forma del transportín
                                    </BodyText>
                                </View>
                                {fieldErrors.acceptsTransportConditions && <ErrorText>{fieldErrors.acceptsTransportConditions}</ErrorText>}
                            </InputWrapper>

                            <InputWrapper>
                                <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginTop: 12 }}>
                                    <CheckBoxCustom
                                        isChecked={formData.acceptsPrivacyPolicy}
                                        onToggle={(val) => handleInputChange('acceptsPrivacyPolicy', val)}
                                    />
                                    <BodyText style={{ flex: 1 }}>
                                        Acepto las{' '}
                                        <Text
                                            style={{ textDecorationLine: 'underline', color: '#007aff' }}
                                            onPress={() => window.open('https://petpass.pro/politicas-de-privacidad', '_blank')}
                                        >
                                            políticas de privacidad
                                        </Text>
                                    </BodyText>
                                </View>
                                {fieldErrors.acceptsPrivacyPolicy && <ErrorText>{fieldErrors.acceptsPrivacyPolicy}</ErrorText>}
                            </InputWrapper>
                        </View>

                        {/* ================= SECCIÓN 5: FIRMA ================= */}
                        <View style={{ marginBottom: 24 }}>
                            <Headline2 style={{ marginBottom: 16, color: colors.azulCorporativo }}>
                                Firma Digital
                            </Headline2>

                            <InputWrapper>
                                <View
                                    style={{
                                        borderWidth: 1,
                                        borderColor: '#ccc',
                                        borderRadius: 8,
                                        backgroundColor: 'white',
                                        padding: 16,
                                        alignItems: 'center'
                                    }}
                                >
                                    <canvas
                                        ref={signatureCanvasRef}
                                        width={400}
                                        height={200}
                                        style={{
                                            border: '1px dashed #ccc',
                                            borderRadius: 4,
                                            cursor: 'crosshair',
                                            width: '100%',
                                            maxWidth: '400px',
                                            height: '200px',
                                            touchAction: 'none',
                                            display: 'block'
                                        }}
                                        onMouseDown={startDrawing}
                                        onMouseMove={draw}
                                        onMouseUp={stopDrawing}
                                        onMouseLeave={stopDrawing}
                                        onTouchStart={startDrawing}
                                        onTouchMove={draw}
                                        onTouchEnd={stopDrawing}
                                        onTouchCancel={stopDrawing}
                                    />

                                    <View style={{ flexDirection: 'row', marginTop: 12, gap: 12 }}>
                                        <TouchableOpacity
                                            onPress={clearSignature}
                                            style={{
                                                paddingVertical: 8,
                                                paddingHorizontal: 16,
                                                borderRadius: 20,
                                                borderWidth: 1,
                                                borderColor: '#ff6b6b',
                                                backgroundColor: 'transparent'
                                            }}
                                        >
                                            <BodyText style={{ color: '#ff6b6b' }}>Borrar</BodyText>
                                        </TouchableOpacity>
                                    </View>
                                </View>
                                {fieldErrors.signature && <ErrorText>{fieldErrors.signature}</ErrorText>}
                            </InputWrapper>
                        </View>
                    </ScrollView>
                </BottomActionContainer>
            </View>
        </AppBackground>
    )
}

// ===== Checkbox inline component =====
const CheckBoxCustom: React.FC<{ isChecked: boolean; onToggle: (checked: boolean) => void }> = ({
    isChecked,
    onToggle
}) => (
    <TouchableOpacity
        onPress={() => onToggle(!isChecked)}
        style={{
            width: 20,
            height: 20,
            borderRadius: 4,
            borderWidth: 2,
            borderColor: '#ccc',
            backgroundColor: isChecked ? '#96ce00' : 'transparent',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 8
        }}
    >
        {isChecked && (
            <View
                style={{
                    width: 10,
                    height: 10,
                    backgroundColor: '#96ce00',
                    borderRadius: 2
                }}
            />
        )}
    </TouchableOpacity>
)

export default AenaTravelEdit
